import { useLanguage } from "@/contexts/LanguageContext";
import { EnhancePromptButton } from "@/components/flowly/EnhancePromptButton/EnhancePromptButton";
import { QuickSuggestions } from "@/components/flowly/QuickSuggestions/QuickSuggestions";

interface ImageGeneratorPromptAreaProps {
  prompt: string;
  onPromptChange: (value: string) => void;
  onEnhancePrompt: () => void;
  isEnhancingPrompt: boolean;
}

export const ImageGeneratorPromptArea = ({
  prompt,
  onPromptChange,
  onEnhancePrompt,
  isEnhancingPrompt,
}: ImageGeneratorPromptAreaProps) => {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-foreground font-bold text-base md:text-lg">
        {t.imageGenerator.editInstructions}
      </h3>

      <div className="flex flex-col gap-4">
        {/* Prompt Input */}
        <div data-tour="prompt-area" className="rounded-3xl border border-brand/20 bg-card p-6 shadow-[0_0_30px_hsl(var(--brand)/0.06)] hover:shadow-[0_0_40px_hsl(var(--brand)/0.1)] hover:border-brand/30 transition-all duration-500">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-semibold text-muted-foreground/80 uppercase tracking-wider">
              {t.imageGenerator.whatToChange}
            </label>
            <div data-tour="enhance-prompt-button">
              <EnhancePromptButton
                onEnhance={onEnhancePrompt}
                isEnhancing={isEnhancingPrompt}
                disabled={!prompt.trim()}
              />
            </div>
          </div>
          <textarea
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            placeholder={t.imageGenerator.promptPlaceholder}
            className="w-full h-32 bg-secondary/30 border border-border/80 rounded-2xl p-4 text-foreground placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:border-brand/50 focus:bg-secondary/50 focus:shadow-[0_0_20px_hsl(var(--brand)/0.08),inset_0_0_20px_hsl(var(--brand)/0.03)] hover:border-border hover:bg-secondary/40 transition-all duration-300"
          />
        </div>

        {/* Quick Suggestions */}
        <div>
          <QuickSuggestions
            suggestions={[
              t.imageGenerator.quickStudioBg,
              t.imageGenerator.quickDramaticLight,
              t.imageGenerator.quickMinimalist,
              t.imageGenerator.quickBlackWhite,
              t.imageGenerator.quickShadows,
            ]}
            onSelect={onPromptChange}
          />
        </div>
      </div>
    </div>
  );
};
